import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { fetchVideos, fetchFestivals } from "../lib/api";
import ShlokaSection from "../components/ShlokaSection";
import VideoCard from "../components/VideoCard";
import NewsletterBlock from "../components/NewsletterBlock";
import LatestFromChannel from "../components/LatestFromChannel";
import Yantra from "../components/Yantra";

const formatDate = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [upcoming, setUpcoming] = useState([]);

    useEffect(() => {
        fetchVideos({ featured: true })
            .then(setFeatured)
            .catch(() => {});
        fetchFestivals({ upcoming: true, limit: 4 })
            .then(setUpcoming)
            .catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden">
                <Yantra
                    className="absolute -right-32 -top-20 w-[720px] h-[720px] hidden lg:block pointer-events-none"
                    opacity={0.14}
                />
                <div className="absolute inset-0 yantra-bg opacity-60 pointer-events-none" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 relative z-10 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <p className="text-[11px] uppercase tracking-[0.35em] text-tattva-muted mb-8">
                            Tattva · तत्त्व · The Essence
                        </p>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-[96px] leading-[0.95] tracking-tight text-tattva-ink">
                            The Science <br />
                            of the{" "}
                            <span className="italic text-tattva-terracotta">
                                Sacred
                            </span>
                            .
                        </h1>
                        <p className="mt-10 max-w-xl text-base md:text-lg text-tattva-muted leading-relaxed">
                            A contemplative home for Hindu festivals, shlokas,
                            and the quiet science behind every ritual. Watch,
                            read, and follow the sacred timeline of 2026.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/videos"
                                data-testid="hero-videos-btn"
                                className="bg-tattva-terracotta text-tattva-bg hover:bg-tattva-terracotta-dark px-8 py-4 uppercase tracking-[0.22em] text-xs transition-colors"
                            >
                                Explore Videos
                            </Link>
                            <Link
                                to="/calendar"
                                data-testid="hero-calendar-btn"
                                className="border border-tattva-ink text-tattva-ink hover:bg-tattva-ink hover:text-tattva-bg px-8 py-4 uppercase tracking-[0.22em] text-xs transition-colors"
                            >
                                Festival Calendar
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="border-l border-tattva-ornament pl-8 py-2">
                            <p className="text-[11px] uppercase tracking-[0.3em] text-tattva-muted mb-3">
                                Currently
                            </p>
                            <p className="font-serif text-2xl text-tattva-ink leading-snug">
                                An evolving library of{" "}
                                <em className="text-tattva-terracotta">
                                    forty-eight
                                </em>{" "}
                                films, twenty-one festivals of{" "}
                                <em className="text-tattva-terracotta">
                                    2026
                                </em>
                                , and seven daily shlokas — growing slowly, with
                                care.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="hair-rule max-w-7xl mx-auto" />

            {/* FEATURED VIDEOS */}
            <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.3em] text-tattva-muted mb-4">
                            On Screen
                        </p>
                        <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-tattva-ink">
                            Featured films & essays
                        </h2>
                    </div>
                    <Link
                        to="/videos"
                        data-testid="view-all-videos-link"
                        className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-tattva-ink hover:text-tattva-terracotta transition-colors"
                    >
                        View all <ArrowUpRight size={16} />
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
                    {featured.slice(0, 3).map((v) => (
                        <VideoCard key={v.id} video={v} />
                    ))}
                </div>
            </section>

            {/* DAILY SHLOKA */}
            <ShlokaSection />

            {/* LATEST FROM YOUTUBE CHANNEL */}
            <LatestFromChannel />

            {/* UPCOMING FESTIVALS */}
            <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.3em] text-tattva-muted mb-4">
                            The Sacred Timeline
                        </p>
                        <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-tattva-ink">
                            Upcoming festivals
                        </h2>
                    </div>
                    <Link
                        to="/calendar"
                        data-testid="view-calendar-link"
                        className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-tattva-ink hover:text-tattva-terracotta transition-colors"
                    >
                        Open calendar <ArrowUpRight size={16} />
                    </Link>
                </div>
                <div className="border-t border-tattva-ornament">
                    {upcoming.map((f) => (
                        <Link
                            key={f.id}
                            to={`/calendar?festival=${f.id}`}
                            data-testid={`upcoming-festival-${f.id}`}
                            className="grid grid-cols-12 gap-4 items-baseline py-8 md:py-10 border-b border-tattva-ornament group"
                        >
                            <div className="col-span-12 md:col-span-3 text-sm uppercase tracking-[0.2em] text-tattva-muted">
                                {formatDate(f.date)}
                            </div>
                            <div className="col-span-12 md:col-span-6">
                                <h3 className="font-serif text-3xl lg:text-4xl text-tattva-ink group-hover:text-tattva-terracotta transition-colors">
                                    {f.name}
                                </h3>
                                {f.sanskrit_name && (
                                    <p className="font-display text-xl text-tattva-terracotta mt-1">
                                        {f.sanskrit_name}
                                    </p>
                                )}
                            </div>
                            <div className="col-span-12 md:col-span-3 text-sm text-tattva-muted md:text-right">
                                {f.tithi}
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            <NewsletterBlock />
        </div>
    );
}
